import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { Button } from '../../components';

import css from './ScrollCompletionWrapper.module.css';

/**
 * A wrapper component that enforces scrolling to the bottom of the content.
 * It implements a "More" button that scrolls down until the bottom is reached,
 * at which point it transforms into a "Close" button.
 *
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - The content to be scrolled (e.g. Terms text)
 * @param {string} props.className - Additional class for the scrollable container
 * @param {string} props.rootClassName - Root class name to override default styles
 * @param {Function} props.onClose - Callback triggered when the Close button is clicked
 * @param {Function} props.onReadComplete - Callback triggered when bottom is reached
 * @param {Function} props.onReadRevoked - Callback triggered if content expands/changes and bottom is lost
 * @param {boolean} props.isLoading - If true, scroll checks are paused
 * @returns {JSX.Element}
 */
const ScrollCompletionWrapper = props => {
  const { 
    children, 
    className,
    rootClassName, 
    onClose, 
    onReadComplete, 
    onReadRevoked,
    isLoading 
  } = props;

  const [isAtBottom, setIsAtBottom] = useState(false);
  const scrollRef = useRef(null);

  const checkIsAtBottom = useCallback(() => {
    const element = scrollRef.current;
    
    // Ignore checks if explicit loading state is true or element is missing
    if (!element || isLoading) return;

    const { scrollHeight, clientHeight, scrollTop } = element;

    // Tolerance of 2px for fractional pixels
    const isScrollable = scrollHeight > clientHeight;
    const hasReachedBottom = Math.abs(scrollHeight - clientHeight - scrollTop) <= 2;

    if (isScrollable) {
      if (hasReachedBottom) {
        if (!isAtBottom) {
          setIsAtBottom(true);
          if (onReadComplete) {
            onReadComplete();
          }
        }
      } else {
        // Content is scrollable but user is NOT at bottom
        if (isAtBottom) {
          setIsAtBottom(false);
          if (onReadRevoked) {
            onReadRevoked();
          }
        }
      }
    } else {
      // Content fits entirely within the view
      // Only mark as read if content has actually loaded (min-height check)
      if (scrollHeight > 50) {
        if (!isAtBottom) {
          setIsAtBottom(true);
          if (onReadComplete) {
            onReadComplete();
          }
        }
      }
    }
  }, [isLoading, isAtBottom, onReadComplete, onReadRevoked]);

  const handleMoreClick = () => {
    const element = scrollRef.current;
    if (!element) return;

    if (isAtBottom) {
      onClose();
    } else {
      // Scroll down by approx 70% of the visible height for better UX
      const scrollAmount = element.clientHeight * 0.7;
      element.scrollBy({ top: scrollAmount, behavior: 'smooth' });
      
      // We need to check bottom status after scroll animation finishes
      setTimeout(() => {
        checkIsAtBottom();
      }, 300);
    }
  };

  // Use ResizeObserver to re-evaluate when content renders/changes size
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      checkIsAtBottom();
    });

    resizeObserver.observe(element);
    
    // Initial check
    checkIsAtBottom();

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkIsAtBottom]);

  const classes = classNames(rootClassName || css.root, className);
  const scrollContainerClasses = css.scrollContainer;

  return (
    <div className={classes}>
      <div 
        ref={scrollRef} 
        className={scrollContainerClasses} 
        onScroll={checkIsAtBottom}
      >
        {children}
      </div>
      
      <div className={css.actions}>
        {!isAtBottom && (
          <p className={css.instruction}>
            <FormattedMessage id="ScrollCompletionWrapper.scrollToBottom" defaultMessage="Please read until the end." />
          </p>
        )}
        <Button 
          onClick={handleMoreClick} 
          className={css.actionButton}
        >
          {isAtBottom ? (
            <FormattedMessage id="ScrollCompletionWrapper.closeModal" defaultMessage="Close" />
          ) : (
            <FormattedMessage id="ScrollCompletionWrapper.more" defaultMessage="More..." />
          )}
        </Button>
      </div>
    </div>
  );
};

export default ScrollCompletionWrapper;